import {
  GiftCardConfigType,
  GiftCardInputFieldType,
  GiftCardProviderResponseType,
  GiftCardProviderType,
  GiftCardsConfigType,
  GiftCardsResponseType,
  GiftCardType,
} from "lib/types/giftCards";

export const getGiftCardCredentials = (card: GiftCardType) => {
  const [cardNumberInput, pinInput] = card?.input_config?.inputs ?? [];
  const valueOf = (input?: GiftCardInputFieldType) =>
    input ? (card?.values?.[input.title] ?? "").trim() : "";

  return { cardNumber: valueOf(cardNumberInput), pin: valueOf(pinInput) };
};

export const getGiftCardErrorMessage = (error: any, fallback: string): string => {
  const responseError = error?.response?.data?.error;
  const candidates = [
    typeof responseError === "string" ? responseError : responseError?.message,
    error?.response?.data?.message,
  ];

  return candidates.find((message) => typeof message === "string" && Boolean(message)) ?? fallback;
};

const VOUCHER_PAYMENT_MODE_BY_PROVIDER: Record<GiftCardProviderType, string> = {
  SHOPIFY: "GIFTCARD",
  GYFTR: "GYFTR",
  QWIKCILVER: "QWIKCILVER",
};

export const getVoucherPaymentMode = (provider: GiftCardProviderType): string =>
  VOUCHER_PAYMENT_MODE_BY_PROVIDER[provider] ?? provider;

export const getProviderFromVoucherPaymentMode = (
  mode?: string,
): GiftCardProviderType | undefined =>
  (Object.keys(VOUCHER_PAYMENT_MODE_BY_PROVIDER) as GiftCardProviderType[]).find(
    (provider) => VOUCHER_PAYMENT_MODE_BY_PROVIDER[provider] === mode,
  );

/** SWR key for a provider's wallet balance, matching the key the Gyftr tile already uses. */
export const GIFT_CARD_BALANCE_KEY = "gc/voucher/balance";

export const defaultGiftCardsConfig: GiftCardsConfigType = {
  isEnabled: false,
  maxUsagePerOrder: 0,
  uiModalConfig: {},
  providers: [],
  unavailabilityReasons: [],
};

/**
 * SWR key for the gift-cards config. Returns null until the checkout exists so
 * `useSWR` stays idle, and doubles as the key every mutation site revalidates.
 */
export const getGiftCardsUrl = (checkoutId?: string): string | null =>
  Boolean(checkoutId) ? `/checkout/${checkoutId}/gift-cards` : null;

const parseGiftCardProvider = (provider: GiftCardProviderResponseType): GiftCardConfigType => ({
  provider: provider?.provider,
  amount_strategy: provider?.amount_strategy ?? "FULL_AMOUNT",
  display_config: {
    label: provider?.display_config?.label ?? "",
    iconUrl: provider?.display_config?.iconUrl ?? provider?.display_config?.image_url,
  },
  input_config: {
    inputs: provider?.input_config?.inputs ?? [],
  },
  gateway_description: provider?.gateway_description ?? "",
  is_active: provider?.is_active ?? true,
  credentials_configured: provider?.credentials_configured ?? true,
  unavailability_reasons: provider?.unavailability_reasons ?? [],
  ...(provider?.rule_group_mapping ? { rule_group_mapping: provider.rule_group_mapping } : {}),
});

export const parseGiftCardsResponse = (response?: GiftCardsResponseType): GiftCardsConfigType => {
  if (!Boolean(response)) return defaultGiftCardsConfig;

  return {
    isEnabled: Boolean(response?.is_enabled),
    maxUsagePerOrder: Number(response?.max_usage_per_order ?? 0),
    uiModalConfig: {
      title: response?.ui_modal_config?.title,
      subtext: response?.ui_modal_config?.subtext,
    },
    providers: (response?.providers ?? [])
      .filter((provider) => Boolean(provider?.provider))
      .map(parseGiftCardProvider),
    unavailabilityReasons: response?.unavailability_reasons ?? [],
  };
};
